<template>
  <button
    type="button"
    class="change-pay-card-small-buttons"
    :class="[`change-pay-card-small-buttons--${button}`, { 'change-pay-card-small-buttons--disabled': disabled }]"
    :disabled="disabled"
    @click.stop="handleClick"
  >
    <span class="change-pay-card-small-buttons__icon" aria-hidden="true">
      <img
        v-if="iconType === 'activity-heart'"
        class="change-pay-card-small-buttons__icon-image"
        :src="activityHeartIcon"
        alt=""
      />
      <span v-else-if="iconType === 'settings-03'" class="change-pay-card-small-buttons__settings-icon">
        <span class="change-pay-card-small-buttons__settings-accent">
          <img :src="settingsAccentIcon" alt="" />
        </span>
        <span class="change-pay-card-small-buttons__settings-glyph">
          <img :src="settingsGlyphIcon" alt="" />
        </span>
      </span>
      <img
        v-else
        class="change-pay-card-small-buttons__icon-image"
        :src="plusIcon"
        alt=""
      />
    </span>
    <span class="change-pay-card-small-buttons__label">{{ displayLabel }}</span>
  </button>
</template>

<script setup>
import { computed } from "vue";

import activityHeartIcon from "./icons/activity-heart.svg";
import plusIcon from "./icons/plus.svg";
import settingsAccentIcon from "./icons/settings-03-accent.svg";
import settingsGlyphIcon from "./icons/settings-03-icon.svg";

const BUTTON_VARIANTS = {
  "evaluate-eligibility": {
    label: "Evaluate Eligibility",
    icon: "activity-heart",
  },
  "propose-changes": {
    label: "Propose Changes",
    icon: "settings-03",
  },
  "propose-first-pay-plan": {
    label: "Propose First Pay Plan",
    icon: "plus",
  },
  "propose-pay-plan": {
    label: "Propose Pay Plan",
    icon: "plus",
  },
};

const props = defineProps({
  button: {
    type: String,
    default: "evaluate-eligibility",
    validator(value) {
      return [
        "evaluate-eligibility",
        "propose-changes",
        "propose-first-pay-plan",
        "propose-pay-plan",
      ].includes(value);
    },
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /** Overrides the variant's default copy (e.g. "Add Pay Plan Proposal"). */
  label: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["click"]);

const displayLabel = computed(() => props.label || BUTTON_VARIANTS[props.button].label);
const iconType = computed(() => BUTTON_VARIANTS[props.button].icon);

function handleClick(event) {
  if (props.disabled) {
    return;
  }
  emit("click", event);
}
</script>

<style scoped>
.change-pay-card-small-buttons {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 0 5px;
  border: 1px solid #d1d5dd;
  border-radius: 6px;
  background: transparent;
  color: #344054;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  cursor: pointer;
  box-sizing: border-box;
}

.change-pay-card-small-buttons--evaluate-eligibility {
  width: fit-content;
}

.change-pay-card-small-buttons--propose-changes {
  width: fit-content;
}

.change-pay-card-small-buttons--propose-first-pay-plan {
  width: fit-content;
}

.change-pay-card-small-buttons--propose-pay-plan {
  width: fit-content;
}

.change-pay-card-small-buttons:hover:not(:disabled) {
  background: #f9fafb;
  color: #18222f;
}

.change-pay-card-small-buttons:active:not(:disabled) {
  background: #eaecf0;
  border-color: #98a2b0;
  color: #182230;
}

.change-pay-card-small-buttons--disabled,
.change-pay-card-small-buttons:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.change-pay-card-small-buttons__icon {
  display: inline-flex;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
}

.change-pay-card-small-buttons__icon-image {
  display: block;
  width: 16px;
  height: 16px;
}

.change-pay-card-small-buttons__settings-icon {
  position: relative;
  width: 16px;
  height: 16px;
  overflow: hidden;
}

.change-pay-card-small-buttons__settings-accent,
.change-pay-card-small-buttons__settings-glyph {
  position: absolute;
  display: block;
}

.change-pay-card-small-buttons__settings-accent {
  top: 16.67%;
  right: 12.5%;
  bottom: 16.67%;
  left: 12.5%;
}

.change-pay-card-small-buttons__settings-accent img {
  position: absolute;
  inset: -6.25% -5.56%;
  display: block;
  width: 100%;
  height: 100%;
  max-width: none;
}

.change-pay-card-small-buttons__settings-glyph {
  inset: 12.5%;
}

.change-pay-card-small-buttons__settings-glyph img {
  position: absolute;
  inset: -5.56%;
  display: block;
  width: 100%;
  height: 100%;
  max-width: none;
}

.change-pay-card-small-buttons__label {
  flex-shrink: 0;
}
</style>
